import React, { useRef } from 'react';
import { clampWidth, WidthRange } from './boardPrefs.ts';

// The two Board side bars (Sites on the left, Bullpen on the right) collapse to a thin rail so the
// board can fill the screen, and an open bar can be dragged to any width in its range. Both share these controls so they look and behave the same: real
// <button>s (keyboard reachable), `aria-expanded` reflecting the state, and a chevron that points
// the way the bar will move.

type Side = 'left' | 'right';

// A left bar opens rightwards (›) and closes leftwards (‹); a right bar is the mirror image.
const OPEN_CHEVRON: Record<Side, string> = { left: '›', right: '‹' };
const CLOSE_CHEVRON: Record<Side, string> = { left: '‹', right: '›' };

interface RailProps {
  /** The bar's name as a user reads it, e.g. "Sites" — the rail shows it vertically. */
  label: string;
  side: Side;
  onExpand: () => void;
}

/** The collapsed bar: one tall button that reopens it. */
export const SidebarRail: React.FC<RailProps> = ({ label, side, onExpand }) => (
  <button
    type="button"
    className={`sidebar-rail sidebar-rail--${side}`}
    onClick={onExpand}
    aria-expanded={false}
    aria-label={`Show ${label.toLowerCase()}`}
    title={`Show ${label.toLowerCase()}`}
  >
    <span className="sidebar-rail-chevron" aria-hidden="true">{OPEN_CHEVRON[side]}</span>
    <span className="sidebar-rail-label" aria-hidden="true">{label.toUpperCase()}</span>
  </button>
);

interface CollapseProps {
  label: string;
  side: Side;
  onCollapse: () => void;
}

/** Sits in an open bar's header and collapses it to its rail. */
export const SidebarCollapseButton: React.FC<CollapseProps> = ({ label, side, onCollapse }) => (
  <button
    type="button"
    className="sidebar-collapse-btn"
    onClick={onCollapse}
    aria-expanded={true}
    aria-label={`Hide ${label.toLowerCase()}`}
    title={`Hide ${label.toLowerCase()}`}
  >
    <span aria-hidden="true">{CLOSE_CHEVRON[side]}</span>
  </button>
);

/** Keyboard step for the resize handle; Shift moves further. */
export const RESIZE_STEP = 16;
export const RESIZE_STEP_LARGE = 64;

interface ResizeProps {
  label: string;
  side: Side;
  width: number;
  range: WidthRange;
  onResize: (width: number) => void;
  /** Tells the layout a drag is in progress, so it can drop its width animation while following the pointer. */
  onDraggingChange?: (dragging: boolean) => void;
}

/**
 * The gutter between an open side bar and the board: drag it to resize the bar. It is a focusable
 * `separator` (the ARIA pattern for a splitter), so the arrow keys resize it too — Shift for bigger
 * steps, Home / End for the narrowest / widest — and a double-click puts the bar back to its default.
 * Dragging toward the board widens the bar, for either side.
 */
export const SidebarResizeHandle: React.FC<ResizeProps> = ({ label, side, width, range, onResize, onDraggingChange }) => {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const towardBoard = side === 'left' ? 1 : -1;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    drag.current = { startX: e.clientX, startWidth: width };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onDraggingChange?.(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const dx = (e.clientX - drag.current.startX) * towardBoard;
    onResize(clampWidth(drag.current.startWidth + dx, range));
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    onDraggingChange?.(false);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? RESIZE_STEP_LARGE : RESIZE_STEP;
    // Arrow keys move the gutter itself: → is wider for the left bar, narrower for the right one.
    const next =
      e.key === 'ArrowRight' ? width + step * towardBoard :
      e.key === 'ArrowLeft' ? width - step * towardBoard :
      e.key === 'Home' ? range.min :
      e.key === 'End' ? range.max :
      null;
    if (next === null) return;
    e.preventDefault();
    onResize(clampWidth(next, range));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label.toLowerCase()}`}
      aria-valuenow={width}
      aria-valuemin={range.min}
      aria-valuemax={range.max}
      tabIndex={0}
      title={`Drag to resize ${label.toLowerCase()} · double-click to reset`}
      className={`sidebar-resize-handle sidebar-resize-handle--${side}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => onResize(range.default)}
      onKeyDown={onKeyDown}
    />
  );
};
